// ============================================================================
// 1. getDashboardAnalytics — Overview Scorecard (StatsBar)
// ============================================================================

export const NEW_LEADS = {
  header: "New Leads",
  description: "Number of new internet leads created in the selected date range.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users, counted by the opportunities's created date.",
};

export const ENGAGED_LEADS = {
  header: "Engaged Leads",
  description: "Of those new internet leads, how many replied back at least once.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users, counted by the opportunities's created date.",
};

export const ENGAGEMENT_RATE = {
  header: "Engagement Rate",
  description: "Percentage of new internet leads that engaged.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users, counted by the opportunities's created date.",
};

// ============================================================================
// 2. getEngagementAnalytics — Messages Sent & Received (EngagementAnalysis)
// ============================================================================

export const ENGAGEMENT_ANALYSIS = {
  header: "Engagement Analysis",
  description:
    "Shows the total messaging activity between the dealership and leads within the selected date range.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users, counted by the message's sent date.",
};

export const SENT_MESSAGES = {
  header: "Sent Messages",
  description: "Total messages sent to leads during the period (includes automated Otto messages).",
};

export const RECEIVED_MESSAGES = {
  header: "Received Messages",
  description: "Total messages received back from leads during the period.",
};

// ============================================================================
// 3. getUserMessageAnalysis — Per-Salesperson Messaging Activity (UserAnalytics)
// ============================================================================

export const MESSAGES_SENT_VS_RECEIVED = {
  header: "Messages Sent vs. Received",
  description:
    "Per-salesperson messaging activity within the selected date range — total outbound (broken into Otto, AI-Assisted, and Manual), inbound replies, and real two-way engagement.",
  metrics: [
    {
      label: "Total Sent",
      description: "All messages the user sent (Otto + AI-assisted + manual).",
    },
    {
      label: "Otto Sent",
      description: "Messages sent automatically by Otto on the user's behalf.",
    },
    {
      label: "AI-Assisted Sent",
      description: "Messages the user sent where the AI suggested the wording.",
    },
    // {
    //   label: "AI-Accepted Sent",
    //   description: "AI-assisted messages sent exactly as the AI suggested.",
    // },
    // {
    //   label: "AI-Modified Sent",
    //   description: "AI-assisted messages the user edited before sending.",
    // },
    {
      label: "Manual Sent",
      description: "Messages the user wrote entirely themselves.",
    },
    {
      label: "Received",
      description: "Replies received from leads.",
    },
    {
      label: "Engagement",
      description:
        "Number of opportunities where there was a two-way conversation (the user messaged and the lead replied).",
    },
  ],
  bottomDescription:
    "Internal filters: messages tied to a lead opportunity only, non-deleted leads, active opportunities, active users. (AI-accepted and AI-modified are tracked inside AI-assisted and aren't double-counted in the total.)",
};

// ============================================================================
// 4. getUserMessageTypeAnalysis — Message Content Types Per Salesperson
// ============================================================================

export const MESSAGE_TYPE = {
  header: "Media Messages",
  description:
    "Breaks down the types of content each salesperson sent to leads within the selected date range.",
  list: {
    listHeader: "Message types tracked:",
    items: [
      // "<strong>Text Messages:</strong> Plain text messages sent to leads",
      "<strong>Image Messages:</strong> Number of image attachments the user sent.",
      "<strong>Video Messages:</strong> Number of video attachments the user sent.",
      "<strong>Doc Messages:</strong> Number of document attachments (PDFs, files) the user sent.",
      // "<strong>Audio Messages:</strong> Messages that included an audio/voice clip attachment",
    ],
  },
};

// ============================================================================
// 5. getUserMessageResponseTimeAnalysis — How Fast Salespeople Reply
// ============================================================================

export const RESPONSE_TIME = {
  header: "Response Time",
  description:
    "Measures how quickly each salesperson replies to lead messages within the selected date range. All times are in minutes.",
  list: {
    listHeader: "Metrics tracked per salesperson:",
    items: [
      "<strong>Best Time:</strong> The user's fastest response time.",
      "<strong>Worst Time:</strong> The user's slowest response time.",
      "<strong>Average Time:</strong> The user's average response time.",
      "<strong>Median Time:</strong> The user's typical (middle) response time.",
      "<strong>Avg Response After Nudge:</strong> How fast they responded after receiving an automated nudge/reminder",
      "<strong>Percentage Change:</strong> How their average response time changed vs. the previous period (negative = improved)",
      "<strong>Trend:</strong> UP / DOWN / SAME — whether response times are improving or getting worse",
    ],
  },
};

export const AVG_RESPONSE_TIME = {
  header: "Avg. Time",
  description:
    "The typical response time, averaged across all replies within the selected date range (in minutes).\n\nThe trend indicator shows improvement or decline compared to the previous period.",
};

export const AVG_RESPONSE_TIME_AFTER_NUDGE = {
  header: "Avg. Response Time After Nudge",
  description:
    "How fast the team responded after receiving an automated nudge or reminder (in minutes).\n\nHelps measure how effectively nudges drive faster follow-ups.",
};

// ============================================================================
// 6. getLeadEngagementAnalysis — Conversation Status Per Salesperson
// ============================================================================

export const LEAD_ENGAGEMENT = {
  header: "Lead Engagement",
  description:
    "Shows the conversation status of leads per salesperson within the selected date range — who's been contacted, who replied, who needs a follow-up, and where conversations stand.",
};

export const UNCONTACTED = {
  header: "Uncontacted",
  description: "Leads that haven't been messaged at all (no messages either way).",
};

export const NOT_RESPONDED = {
  header: "No response from initial contact",
  description: "The user messaged the lead, but the lead hasn't replied yet.",
};

export const RESPONDED = {
  header: "Responded",
  description: "The lead has replied at least once.",
};

export const NOT_ENGAGED = {
  header: "Not Engaged",
  description:
    "There's been some back-and-forth, but a light conversation (fewer than two exchanges each way).",
};

export const ENGAGED = {
  header: "Engaged",
  description: "A real back-and-forth conversation (at least two messages each way).",
};

export const WAITING_FOR_LEAD_REPLY = {
  header: "Waiting For Lead Reply",
  description: "The user sent the most recent message — waiting on the lead.",
};

export const NEEDS_YOUR_REPLY = {
  header: "Needs Your Reply",
  description: "The lead sent the most recent message — the user needs to respond.",
};

export const NO_RESPONSE_NEEDED = {
  header: "No Response Needed",
  description: "The lead is marked as not needing a response.",
};

// ============================================================================
// 7. getAppointmentAnalysis — Appointment Trends & Booking Rate
// ============================================================================

export const APPOINTMENT_ANALYSIS = {
  header: "Digital Appointment Analysis",
  description: "Tracks digital appointment trends and booking rate within the selected date range.",
  list: {
    listHeader: "Statuses tracked:",
    items: [
      "<strong>Scheduled:</strong> Appointments that were booked",
      "<strong>Confirmed:</strong> Appointments the lead confirmed they will attend",
      "<strong>Rescheduled:</strong> Appointments that were moved to a different time",
      "<strong>No Showed:</strong> Appointments where the lead did not show up",
      "<strong>Showed:</strong> Appointments where the lead came in",
      "<strong>Sold:</strong> Appointments where the lead came in and purchased a vehicle",
      "<strong>Cancelled:</strong> Appointments that were cancelled",
    ],
  },
  bottomDescription:
    "Internal filters always applied: internet leads only, active opportunities, non-deleted leads, and active users. Use the date toggle to switch between the booking date (when the appointment was created) and the appointment due date (when it's scheduled to happen).",
};

export const NEW_LEAD_APPOINTMENT = {
  header: "New Leads",
  description: "Number of new internet leads created in the selected date range.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users, counted by the opportunities's created date.",
};

export const BOOKED_APPOINTMENTS = {
  header: "Booked Appointments",
  description:
    "Appointments that were Scheduled, Confirmed, Rescheduled, Showed, No-Showed, or Sold within the selected date range.",
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users. Use the date toggle to count by the booking date (when the appointment was created) or the appointment due date (when it's scheduled to happen).",
};

export const BOOKED_APPOINTMENT_RATE = {
  header: "% of Booked Appointments",
  description: "The percentage of new leads that turned into an appointment.",
  list: {
    listHeader: "Calculated as:",
    items: ["<em>(Booked Appointments ÷ New Leads) × 100</em>"],
  },
  bottomDescription:
    "Internal filters always applied: internet/digital sources only, assigned to active users. Use the date toggle to count by the booking date or the appointment due date.",
};

// ============================================================================
// 8. getAppointmentSalesperson — Appointments Per Salesperson
// ============================================================================

export const APPOINTMENTS_BY_SALES_PERSON = {
  header: "Appointments by Sales Person",
  description:
    "Shows appointment activity and outcomes per salesperson within the selected date range — helping evaluate individual performance and follow-up effectiveness.",
  metrics: [
    {
      label: "Default view (Waterfall)",
      description:
        "By default, the table shows Waterfall mode — it tracks the full journey of every appointment scheduled in the date range, so a single appointment can appear across multiple status columns (rescheduled, showed, no-showed, confirmed, etc.) as it moved through them.",
    },
    {
      label: "“Current Status” toggle",
      description:
        "Enable the Current Status toggle to switch to a real-time snapshot — each appointment is counted exactly once, in whichever status it currently sits.",
    },
  ],
  list: {
    listHeader: "Metrics tracked per salesperson:",
    items: [
      "<strong>Total Appointments:</strong> Total number of appointments this salesperson had in the period.",
      "<strong>Scheduled:</strong> Number of appointments booked.",
      "<strong>Rescheduled:</strong> Number of appointments moved to a new time.",
      "<strong>Showed:</strong> Number of appointments where the lead showed up.",
      "<strong>Cancelled:</strong> Number of appointments that were cancelled.",
      "<strong>No-Showed:</strong> Number of appointments where the lead didn't show up.",
      "<strong>Confirmed:</strong> Number of appointments the lead confirmed.",
      "<strong>Sold:</strong> Number of opportunities that became a sale after the appointment was booked.",
    ],
  },
};

export const OPPORTUNITY_BY_SALES_PERSON = {
  header: "Opportunity by Sales Person",
  description:
    "Per salesperson, how their opportunities are distributed across statuses, shown as a hierarchy (statuses roll up into parent statuses). Includes per-status totals across everyone.",
  metrics: [
    {
      label: "Default view (Waterfall)",
      description:
        "By default, the table shows Waterfall mode — for each column, it counts opportunities that moved into that status during the period. A single opportunity can appear in multiple status columns if it transitioned through them.",
    },
    {
      label: "“Current Status” toggle",
      description:
        "Enable the Current Status toggle to switch to a real-time snapshot — it reads the current status on each active opportunity, so every opportunity appears in exactly one status column (wherever it sits right now).",
    },
  ],
  list: {
    listHeader: "Metrics tracked per salesperson:",
    items: [
      "<strong>Total Opportunities:</strong> All active opportunities assigned to this salesperson.",
      "<strong>(Each status / sub-status):</strong> Number of the salesperson's opportunities in that status. In Waterfall view this counts opportunities that moved into the status during the period; in Normal view it counts their current status.",
      "<strong>Total Count:</strong> The salesperson's total across the shown statuses.",
    ],
  },
  bottomDescription:
    "Both modes build the same status tree hierarchy and compute the same column-footer totals across all salespeople.",
};

// ============================================================================
// 9. getUserReviewAnalysis — Review Invites Per Salesperson
// ============================================================================

export const REVIEW_REQUEST_ANALYSIS = {
  header: "Review Request Analysis",
  description:
    "Shows review invite activity and outcomes per salesperson within the selected date range — helping identify missed opportunities to request customer feedback.",
};

export const INVITE_SENT = {
  header: "Invites Sent",
  description: "A review invite was sent, but no review posted yet.",
};

export const REVIEW_COMPLETED = {
  header: "Reviews Completed",
  description: "The lead posted a review.",
};

export const INVITE_NOT_SENT = {
  header: "Invites Not Sent",
  description: "A sold lead that never received a review invite.",
};

// ============================================================================
// 10. getReviewStatusAnalysis — Review Status Per Lead
// ============================================================================

export const REVIEW_STATUS = {
  header: "Review Status",
  description:
    "Shows the review status of individual leads within the selected date range — whether a review was completed, invited, or missed entirely.",
  list: {
    listHeader: "Statuses tracked:",
    items: [
      "<strong>Review Completed:</strong> This lead actually posted an online review",
      "<strong>Invite Sent:</strong> A review invite was sent but the lead hasn't posted a review yet",
      "<strong>Invite Not Sent:</strong> This lead was sold but no review invite has been sent — a missed opportunity",
    ],
  },
  bottomDescription:
    "The table also shows the lead name, responsible salesperson, sold date, and the latest review-related activity date.",
};

// ============================================================================
// Performance by Source (getDataBySource)
// ============================================================================

export const SOURCE_GRAPH = {
  header: "Performance by Source",
  description:
    "Shows how different lead sources contribute to leads, appointments, and customer responses within the selected date range.",
  list: {
    listHeader: "This section includes:",
    items: [
      "<strong>Appointments by Source:</strong> How leads convert into appointments and progress across stages such as scheduled, confirmed, attended, rescheduled, cancelled, and no-show",
      "<strong>Leads by Source:</strong> Volume of leads generated from each channel and their contact status",
      "<strong>Communication by Source:</strong> How actively leads from each source are engaging through replies",
    ],
  },
  bottomDescription:
    "Helps evaluate source quality, conversion efficiency, and engagement performance — making it easier to identify which channels drive the most valuable outcomes.",
};

// ============================================================================
// Activity by Sales Person (getActivitiesBySalesperson)
// ============================================================================

export const ACTIVITY_BY_SALES_PERSON = {
  header: "Activity by Sales Person",
  description:
    "Per-salesperson task & activity workload within the selected date range — what each user created, what's assigned to them, and the current upcoming / overdue / completed split.",
  list: {
    listHeader: "Columns in this table:",
    items: [
      "<strong>Created:</strong> Activities this user created within the selected date range",
      "<strong>Assigned:</strong> Activities currently assigned to this user (whether they created them or someone else did)",
      "<strong>Upcoming:</strong> Active activities with a due date in the future",
      "<strong>Overdue:</strong> Active activities whose due date has passed without completion",
      "<strong>Completed:</strong> Activities this user has finished",
    ],
  },
};

// ============================================================================
// Communication by Sales Person (getMessagesBySalesperson)
// ============================================================================

export const COMMUNICATION_BY_SALES_PERSON = {
  header: "Communication by Sales Person",
  description:
    "Per-salesperson communication breakdown across text, email, and call channels within the selected date range. Sends in each channel are split by source — Otto (automated), AI-Assisted, and Manual — so you can see exactly how each rep is mixing automation and personal touch.",
  list: {
    listHeader: "Columns in this table:",
    items: [
      "<strong>Otto Sent:</strong> Text messages sent automatically by Otto AI to the lead.",
      "<strong>AI-Assisted Sent:</strong> Text messages the user sent using an AI suggestion.",
      // "<strong>AI Accepted:</strong> AI-assisted texts sent exactly as suggested, with no edits.",
      // "<strong>AI Modified:</strong> AI-assisted texts the user edited before sending.",
      "<strong>Manual Sent:</strong> Text messages the user typed without any AI help.",
      "<strong>Text Received:</strong> Text messages received from the lead.",
      "<strong>Email Otto Sent:</strong> Emails sent automatically by Otto AI to the lead.",
      "<strong>Email AI-Assisted Sent:</strong> Emails the user sent using an AI suggestion.",
      // "<strong>Email AI Accepted:</strong> AI-assisted emails sent exactly as suggested, with no edits.",
      // "<strong>Email AI Modified:</strong> AI-assisted emails the user edited before sending.",
      "<strong>Email Manual Sent:</strong> Emails the user typed without any AI help.",
      "<strong>Email Received:</strong> Emails received from the lead.",
      "<strong>Outbound Calls:</strong> Calls made to the lead.",
      "<strong>Inbound Calls:</strong> Calls received from the lead.",
      "<strong>Total Activity:</strong> Total of all sent and received messages, emails, and calls.",
    ],
  },
  bottomDescription:
    "Internal filters: messages tied to a lead opportunity only, non-deleted leads, active opportunities, active users. (AI-accepted and AI-modified are tracked inside AI-assisted and aren't double-counted in the total.)",
};

// ============================================================================
// Performance by Source — MESSAGE dataType (getDataBySource)
// ============================================================================

export const SOURCE_GRAPH_MESSAGE = {
  header: "Performance by Source",
  description:
    "How leads from each source perform across text, email, and call activity within the selected date range. Sends in each channel are split by source — Otto (automated), AI-Assisted, and Manual — so you can see how each lead source is being worked.",
  list: {
    listHeader: "Columns in this view:",
    items: [
      "<strong>Opportunities:</strong> Leads from this source that had a two-way conversation in this period.",
      "<strong>Otto Sent:</strong> Text messages sent automatically by Otto AI to leads from this source.",
      "<strong>AI-Assisted Sent:</strong> Text messages the user sent using an AI suggestion.",
      // "<strong>AI Accepted:</strong> AI-assisted texts sent exactly as suggested, with no edits.",
      // "<strong>AI Modified:</strong> AI-assisted texts the user edited before sending.",
      "<strong>Manual Sent:</strong> Text messages the user typed without any AI help.",
      "<strong>Text Received:</strong> Text messages received from leads of this source.",
      "<strong>Email Otto Sent:</strong> Emails sent automatically by Otto AI to leads from this source.",
      "<strong>Email AI-Assisted Sent:</strong> Emails the user sent using an AI suggestion.",
      // "<strong>Email AI Accepted:</strong> AI-assisted emails sent exactly as suggested, with no edits.",
      // "<strong>Email AI Modified:</strong> AI-assisted emails the user edited before sending.",
      "<strong>Email Manual Sent:</strong> Emails the user typed without any AI help.",
      "<strong>Email Received:</strong> Emails received from leads of this source.",
      "<strong>Outbound Calls:</strong> Calls made to leads of this source.",
      "<strong>Inbound Calls:</strong> Calls received from leads of this source.",
    ],
  },
  bottomDescription:
    "For the same date/user, Otto / AI-Assisted / Manual counts here match the Communication by Sales Person table (text channel only).",
};

// ============================================================================
// Detailed Media Report — single consolidated info drawer.
// Lists every column's description plus the cross-cutting rules. Surfaced
// via the one info icon next to the "Detailed media report" page title;
// there are no per-column info icons.
// ============================================================================

export const MEDIA_DETAIL_REPORT_INFO = {
  header: "Detailed media report",
  description: "Each row is one sales rep. Here's what each column means.",
  sections: [
    {
      title: "User",
      items: [
        "<strong>User</strong> — The team member who sent the messages.",
        "<strong>Department</strong> — The department this user belongs to.",
      ],
    },
    {
      title: "Image metrics",
      items: [
        "<strong>Images Sent</strong> — How many image attachments this user sent to leads.",
        "<strong>Image Views</strong> — How many of those images the lead actually opened.",
        "<strong>Image View Rate</strong> — The percentage of images sent that were opened by leads.",
        "<strong>Images Downloaded</strong> — How many images the lead saved or downloaded.",
        "<strong>Image Download Rate</strong> — The percentage of images sent that were downloaded by leads.",
      ],
    },
    {
      title: "Lead opportunities & video reach",
      items: [
        "<strong>Opps Assigned</strong> — The number of new internet leads assigned to this user during the selected period. Only internet-source leads count here.",
        "<strong>Videos Sent</strong> — How many video attachments this user sent to leads.",
        "<strong>Opportunities with video</strong> — Assigned internet lead opportunities sent at least one video.<br />Rate = leads with video ÷ leads assigned.",
      ],
    },
    {
      title: "Video engagement",
      items: [
        "<strong>Video Views</strong> — How many videos the lead opened.",
        "<strong>Video View Rate</strong> — The percentage of videos sent that were opened by leads.",
        "<strong>Watch Rate</strong> — The percentage of videos sent that the lead actually played (not just opened, but pressed play and watched some of it).",
        "<strong>Videos Downloaded</strong> — How many videos the lead saved or downloaded.",
        "<strong>Video Download Rate</strong> — The percentage of videos sent that were downloaded by leads.",
        "<strong>Avg Video Duration (sec)</strong> — The average length of the videos the user sent.",
        "<strong>Avg Watch Duration (sec)</strong> — On average, how many seconds the lead actually spent watching each video they played.",
      ],
    },
    {
      title: "OttoVin metrics",
      items: [
        "<strong>OttoVin link sent</strong> — How many OttoVin vehicle-detail links this user sent to leads.",
        "<strong>OttoVin link opened</strong> — How many of those OttoVin links the lead actually opened.<br />Rate = links opened ÷ links sent.",
        "<strong>Opportunities with OttoVin</strong> — Assigned internet lead opportunities that were sent at least one OttoVin link.<br />Rate = OttoVin links sent ÷ unique opportunities.",
      ],
    },
    {
      title: "Document metrics",
      items: [
        "<strong>Docs Sent</strong> — How many document attachments (e.g. PDFs) this user sent.",
        "<strong>Docs Opened</strong> — How many of those documents the lead opened.",
        "<strong>Doc Open Rate</strong> — The percentage of documents sent that were opened by leads.",
        "<strong>Docs Downloaded</strong> — How many documents the lead saved or downloaded.",
        "<strong>Doc Download Rate</strong> — The percentage of documents sent that were downloaded by leads.",
      ],
    },
    {
      title: "Overall engagement",
      items: [
        // "<strong>MMS Sent</strong> — The total number of MMS messages this user sent (each message counts once, no matter how many images, videos, or docs are inside).",
        // "<strong>MMS Engaged</strong> — How many of those MMS messages got a reply back from the lead.",
        // "<strong>Replies Received</strong> — The total number of replies leads sent back after receiving an MMS from this user.",
        "<strong>Engagement Rate</strong> — The percentage of MMS messages that got a reply. A higher number means the user's messages are starting more conversations.",
      ],
    },
  ],
};
